import { useEffect, useState } from "react";
import { isSupabaseConfigured } from "@/lib/supabase";
import { getFamilyComplianceSummary } from "@/lib/documents";
import { listIncidents } from "@/lib/incidents";

/**
 * Parent-side badge counts that drive the dashboard's conditional cards
 * AND the bottom-tab/sidebar nav badges.
 *
 * Returns:
 *   missingDocs           — count of required docs missing/rejected/expired
 *   unacknowledgedIncidents — count of incidents the parent hasn't ack'd yet
 *   loaded                — false until the first fetch resolves
 *
 * Cheap enough to call on every page mount (small queries, RLS-safe).
 * If either query fails the relevant count stays at 0 — we don't want a
 * transient backend hiccup to flash badges/cards inconsistently.
 */
export default function useParentBadges(profileId) {
  const [counts, setCounts] = useState({
    missingDocs: 0,
    unacknowledgedIncidents: 0,
    loaded: false,
  });

  useEffect(() => {
    if (!profileId || !isSupabaseConfigured) {
      setCounts({ missingDocs: 0, unacknowledgedIncidents: 0, loaded: true });
      return;
    }
    let alive = true;

    const compute = async () => {
      const [docs, inc] = await Promise.allSettled([
        getFamilyComplianceSummary(profileId),
        listIncidents({ role: "parent", profileId }),
      ]);
      if (!alive) return;

      // Missing-required is the action-able subset; rejected/expired-required
      // are also action-able from the parent's perspective. We surface their
      // sum so the badge represents "things you need to handle".
      let missingDocs = 0;
      if (docs.status === "fulfilled" && docs.value) {
        missingDocs =
          (docs.value.missingRequired || 0) +
          (docs.value.rejectedCount || 0) +
          (docs.value.expiredRequired || 0);
      }

      let unacknowledgedIncidents = 0;
      if (inc.status === "fulfilled") {
        const rows = inc.value || [];
        unacknowledgedIncidents = rows.filter((r) => {
          // Pre-acknowledgement = anything not yet ack'd by THIS parent.
          // Live schema stores parent_acknowledged_by/at; demo mode uses
          // status === "open" or "resolved" with no ack timestamp.
          if (r.parent_acknowledged_at) return false;
          if (r.acknowledged_at) return false;
          return r.status !== "voided";
        }).length;
      }

      setCounts({ missingDocs, unacknowledgedIncidents, loaded: true });
    };

    compute();
    return () => {
      alive = false;
    };
  }, [profileId]);

  return counts;
}
